import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import { checkouts, invoices, plans, subscriptions } from 'stripe';
import API, { EVersion } from './lib/API';

import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { Severity } from '@sentry/types';

// Addons available
export enum EAddons {
  Podorun = 'podorun',
  Podokine = 'podokine',
}

export type TAddon = {
  id: EAddons;
  plans: plans.IPlan['id'][];
};

// Service options type
export type TOptions = {
  key: string;
};

// State
export type TState = {
  addons: TAddon[];
  plans: plans.IPlan[];
  invoices: invoices.IInvoice[];
  subscription: subscriptions.ISubscription | null;

  pending: boolean;
  error: Error | null;
};

enum EEntities {
  Plans = 'plans',
  Addons = 'addons',
  Invoices = 'invoices',
  Subscription = 'subscription',
}

export const SET_PLANS = 'SET_PLANS';
export const SET_ADDONS = 'SET_ADDONS';
export const SET_INVOICES = 'SET_INVOICES';
export const SET_SUBSCRIPTION = 'SET_SUBSCRIPTION';
export const SET_PENDING = 'SET_PENDING';
export const SET_ERROR = 'SET_ERROR';

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class EntitiesService extends AbstractService<TOptions, TState> {
  private readonly stripe: any;
  private api!: API;

  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'addons');

    if (!(window as any).Stripe) throw new Error('Missing Stripe CDN');
    this.stripe = (window as any).Stripe(this.options.key);
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): TState {
    return {
      addons: [],
      plans: [],
      invoices: [],
      subscription: null,

      pending: false,
      error: null,
    };
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>): Promise<void> {
    await super.init(Vue, store);
    const _this = this;

    this.api = new API(_this.Vue);

    this.$register(this.defaultState, {
      mutations: {
        [SET_PLANS](state, plans: TState['plans']) {
          state.plans = plans;
        },
        [SET_ADDONS](state, addons: TState['addons']) {
          state.addons = addons;
        },
        [SET_INVOICES](state, invoices: TState['invoices']) {
          state.invoices = invoices;
        },
        [SET_SUBSCRIPTION](state, subscription: TState['subscription']) {
          state.subscription = subscription;
        },
        [SET_PENDING](state, pending: TState['pending']) {
          state.pending = pending;
        },
        [SET_ERROR](state, err: TState['error']) {
          state.error = err;
        },
      },
      actions: {
        async fetch({ commit, dispatch }) {
          // Start timer
          const _t0 = performance.now();
          // Bus
          _this.Vue.prototype.$pdc.bus.emitDebug(`addons:sync-pending`);
          // Log
          _this.logger.log(`Start synchronization ...`);
          try {
            // Up pending flag
            commit('SET_PENDING', true);
            // Fetch all entities
            await Promise.all([dispatch('fetchPlans'), dispatch('fetchAddons'), dispatch('fetchSubscription')]);
            // Fetch invoices
            dispatch('fetchInvoices');

            // Stop timer
            const _t1 = performance.now();
            // Bus
            _this.Vue.prototype.$pdc.bus.emitDebug(`addons:sync-completed`);
            // Log
            _this.logger.success(`Synchronization completed in ${Math.round(_t1 - _t0)}ms`);
          } catch (e) {
            // Commit error
            commit('SET_ERROR', e);
            // Bus
            _this.Vue.prototype.$pdc.bus.emitDebug(`addons:sync-failed`, e);
            // Log
            _this.logger.fatal(`Failed to sync`);
            // Sentry
            _this.Vue.prototype.$sentry.captureMessage(e.message, Severity.Fatal);
          } finally {
            // Down pending flag
            commit('SET_PENDING', false);
          }
        },
        async fetchPlans() {
          await _this.fetch(EEntities.Plans);
        },
        async fetchAddons() {
          await _this.fetch(EEntities.Addons);
        },
        async fetchInvoices() {
          await _this.fetch(EEntities.Invoices);
        },
        async fetchSubscription() {
          await _this.fetch(EEntities.Subscription);
        },
      },
      getters: {
        getPlan: (state) => (planId: plans.IPlan['id']) => {
          return state.plans.find((x) => x.id === planId);
        },
        getActivePlan: (state): plans.IPlan['id'] | null => {
          return !state.subscription || !state.subscription.plan ? null : state.subscription.plan.id;
        },
        getPlanAddons: (state) => (planId: plans.IPlan['id']) => {
          return state.addons.filter((x) => x.plans.includes(planId)).map((x) => x.id);
        },
        getInvoice: (state) => (invoiceId: invoices.IInvoice['id']) => {
          return state.invoices.find((x) => x.id === invoiceId);
        },
        getActiveAddons: (state): EAddons[] => {
          return !state.subscription || !state.subscription.plan
            ? []
            : state.addons.filter((x: any) => x.plans.includes(state.subscription!.plan!.id)).map((x: any) => x.id);
        },
      },
    });
  }

  /**
   * Fetching helper
   * @param entity
   */
  private async fetch(entity: EEntities) {
    // Start timer
    const _t0 = performance.now();
    // Bus
    this.Vue.prototype.$pdc.bus.emitDebug(`addons:${entity}:sync-pending`);
    // Log
    this.logger.withTag(entity).log(`Fetching ...`);
    try {
      // Fetch
      const response = await this.api.get(EVersion.V1, entity);
      // Save
      this.$commit(`SET_${entity.toUpperCase()}`, response.data.result);
      // Stop timer
      const _t1 = performance.now();
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug(`addons:${entity}:sync-completed`);
      // Log
      this.logger.withTag(entity).success(`Synced in ${Math.round(_t1 - _t0)}ms`);
    } catch (e) {
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug(`addons:${entity}:sync-failed`, e);
      // Log
      this.logger.withTag(entity).fatal(`Failed to sync`);
      // Throw
      throw e;
    }
  }

  /**
   * Sync subscriptions and products
   */
  async sync(): Promise<void> {
    await this.$dispatch('fetch');
  }

  public async redirectToCheckout(sessionId: checkouts.sessions.ICheckoutSession['id']): Promise<void> {
    return this.stripe.redirectToCheckout({ sessionId });
  }

  async subscribe(planId: plans.IPlan['id']) {
    // Create session ID
    const response = await this.api.post(EVersion.V1, 'checkout/session', {
      planId: planId,
    });
    // Redirect to checkout hosted page
    await this.redirectToCheckout(response.data.result.id);
  }

  async switchTo(subscriptionId: subscriptions.ISubscription['id'], planId: plans.IPlan['id']) {
    // Try to patch subscription ...
    const response = await this.api.post(
      EVersion.V1,

      `subscription/${subscriptionId}/switch`,
      {
        planId: planId,
      },
    );
    // Save patched subscription
    this.$commit(SET_SUBSCRIPTION, response.data.result);
  }

  async cancel(subscriptionId: subscriptions.ISubscription['id'], force: boolean) {
    // Try to patch subscription ...
    const response = await this.api.post(EVersion.V1, `subscription/${subscriptionId}/cancel`, { force });
    // Save patched subscription
    if (response.data.result.status === 'canceled') {
      this.$commit(SET_SUBSCRIPTION, null);
    } else {
      this.$commit(SET_SUBSCRIPTION, response.data.result);
    }
    // Fetch invoices
    await this.$dispatch('fetchInvoices');
  }

  async reactivate(subscriptionId: subscriptions.ISubscription['id']) {
    // Try to patch subscription ...
    const response = await this.api.post(EVersion.V1, `subscription/${subscriptionId}/reactivate`);
    // Save patched subscription
    this.$commit(SET_SUBSCRIPTION, response.data.result);
  }
}
